import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { QRCode } from '@usf/alert-types';
import { QR_CODE_ACTIONS, TRACE_CONTEXT } from '@usf/ngrx-alerts';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../../user/services';
import { MorePageViewModel, NewsDropdownData } from '../models/more';
import { PanAppState } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { SiteCustomizationProfile } from '@usf/user-types/site-customization';
import { CustomizationOptions } from '@shared/constants/customizations.enum';
import { BetterBuysService } from '../../better-buys/services/better-buys.service';
import { PayInvoiceUser } from '@app/user/constants/user-profile.constants';
import { CustomerRebatesService } from '@shared/services/customer-rebates/customer-rebates.service';

@Injectable({
  providedIn: 'root',
})
export class MoreService {
  constructor(
    private store: Store,
    private userService: UserService,
    private panAppState: PanAppState,
    private betterBuysService: BetterBuysService,
    private customerRebatesService: CustomerRebatesService,
  ) {}

  getMoreVM$(): Observable<MorePageViewModel> {
    const newsLinkFeatureFlag = this.panAppState.feature$([
      FEATURES.split_global_user_customizations.name,
    ]);
    const superUserFlag$ = this.panAppState.feature$([
      FEATURES.split_global_alerts_super_user.name,
    ]);
    const superUserImportOrderFlag$ = this.panAppState.feature$([
      FEATURES.split_global_order_super_user_import_order.name,
    ]);
    const isSuperUser$ = this.userService.isSuperUser$();
    const sellerShowcaseFlag$ = this.panAppState.feature$([
      FEATURES.split_global_seller_showcase_products.name,
    ]);
    const rebatesFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_rebates_dropdown.name,
    ]);
    const accessToken$ = this.panAppState.accessToken$;
    return combineLatest([
      this.userService.showMoxeInventory$(),
      this.userService.userProfiles$(),
      this.userService.userCustomizations$(),
      newsLinkFeatureFlag,
      this.panAppState?.user$,
      this.betterBuysService.getBetterBuysFlag(),
      this.userService.showBusinessAnalytics$(),
      this.userService.showTv360$(),
      superUserFlag$,
      superUserImportOrderFlag$,
      isSuperUser$,
      this.userService.showMPPMoxe$(),
      accessToken$,
      sellerShowcaseFlag$,
      this.customerRebatesService.getTakeoutOptions(),
      rebatesFlag$,
    ]).pipe(
      map(
        ([
          showInventory,
          userProfiles,
          customizationData,
          newsLinkFeatureFlag,
          user,
          betterBuysFlag,
          showBA,
          showTv360,
          superUserFlag,
          superUserImportOrderFlag,
          isSuperUser,
          showMoxeMpp,
          accessToken,
          sellerShowcaseFlag,
          takeoutOptions,
          isRebatesEnabled,
        ]) => {
          const hideInvoiceAndPayments = userProfiles?.find(
            userProf => userProf.key === 'HideInvoiceAndPayments',
          );
          const payInvoiceUser = userProfiles?.find(
            userProf => userProf.key === PayInvoiceUser,
          );
          const userSiteRole = userProfiles?.find(
            userProf => userProf.key === 'ACCESS_ROLE',
          )?.value;
          const newsDropdown = newsLinkFeatureFlag && !!customizationData;
          const newsData = this.newsPageData(customizationData);
          const showRecipes = superUserFlag && isSuperUser ? false : true;
          const showBusinessAnalytics =
            superUserFlag && isSuperUser ? false : showBA;
          const showSellerShowcaseProducts =
            sellerShowcaseFlag && this.userService.isInternalUser(accessToken);
          const isCustomerAdmin = this.userService.isCustomerAdmin(accessToken);
          const viewModel: MorePageViewModel = {
            hideInvoiceAndPayments: !!hideInvoiceAndPayments
              ? hideInvoiceAndPayments.value === 'Y'
              : false,
            canPayInvoice: payInvoiceUser?.value === 'Y' ? true : false,
            isSuperUser,
            superUserImportOrderFlag,
            showInventory,
            userId: user.ecomUserId?.toString(),
            userName: user?.userId,
            showNewsLink: newsDropdown,
            newsPageData: newsData,
            betterBuysFlag,
            showBusinessAnalytics,
            showTv360,
            userSiteRole,
            showRecipes,
            showMoxeMpp,
            showSellerShowcaseProducts,
            isCustomerAdmin,
            takeoutOption: isRebatesEnabled && takeoutOptions?.[0],
          };
          return viewModel;
        },
      ),
    );
  }

  newsPageData(newsPageData: SiteCustomizationProfile): NewsDropdownData {
    let data: NewsDropdownData = {
      label: '',
      url: '',
    };
    newsPageData?.properties?.forEach(customization => {
      if (customization?.name === CustomizationOptions.newsMenu) {
        data.label = customization?.value;
      }
      if (customization?.name === CustomizationOptions.newsMenuUrl) {
        data.url = customization?.value;
      }
    });
    return data;
  }

  trackQRCode(qrCodeString: string, qrCodeObj: QRCode) {
    this.store.dispatch(
      QR_CODE_ACTIONS.trackQRCode({
        qrCodeString: qrCodeString,
        qrCodeRequest: qrCodeObj,
        tracking: {
          tracing: {
            data: {
              traceContext: TRACE_CONTEXT.trackQRCode,
              isStartOfTrace: true,
              isEndOfTrace: false,
            },
          },
        },
      }),
    );
  }
}
